import React, { useEffect, useState } from 'react'
import { getAPIBaseURL } from '../../services/helpers'
import { getData } from '../../services/request'
import { Link } from 'react-router-dom'
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ShoppingBagIcon,
  TicketIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

const AdminHomePage = () => {
  const [stats, setStats] = useState({
    centers: 0,
    attendees: 0,
    events: 0,
    orders: 0,
    revenue: 0,
    tickets: 0,
    disputes: 0
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true)
        setError('')
        
        // Use reports endpoint which provides comprehensive stats
        const reportsUrl = `${getAPIBaseURL()}/v1/reports`
        const token = localStorage.getItem('token')
        const headers = { 'token': token }

        const reportsRes = await getData(reportsUrl, headers, {})

        if (reportsRes.error === '' && reportsRes.result.status === 200) {
          const reportsData = reportsRes.result.data?.data
          if (reportsData) {
            setStats({
              centers: reportsData.users?.centers?.total || 0,
              attendees: reportsData.users?.attendees?.total || 0,
              events: reportsData.events?.total || 0,
              orders: reportsData.orders?.total || 0,
              revenue: reportsData.payments?.revenue?.total || 0,
              tickets: reportsData.tickets?.total || 0,
              disputes: 0 // Disputes count not in reports, fetch separately if needed
            })
          }
        } else {
          // Fallback: try analytics summary
          const analyticsUrl = `${getAPIBaseURL()}/v1/analytics/summary`
          const analyticsRes = await getData(analyticsUrl, headers, {})
          
          if (analyticsRes.error === '' && analyticsRes.result.status === 200) {
            const analyticsData = analyticsRes.result.data?.data
            if (analyticsData) {
              setStats(prev => ({
                ...prev,
                orders: analyticsData.ordersCount || 0,
                revenue: analyticsData.gmv || 0,
                tickets: analyticsData.ticketsCount || 0
              }))
            }
          }

          // Fetch user counts separately
          const usersUrl = `${getAPIBaseURL()}/v1/users`
          const [centerRes, attendeeRes] = await Promise.all([
            getData(usersUrl, headers, { role: 'CENTER', limit: 0 }),
            getData(usersUrl, headers, { role: 'ATTENDEE', limit: 0 })
          ])

          if (centerRes.error === '' && centerRes.result.status === 200) {
            const centersData = centerRes.result.data?.data
            if (Array.isArray(centersData)) {
              setStats(prev => ({ ...prev, centers: centersData.length }))
            }
          }

          if (attendeeRes.error === '' && attendeeRes.result.status === 200) {
            const attendeesData = attendeeRes.result.data?.data
            if (Array.isArray(attendeesData)) {
              setStats(prev => ({ ...prev, attendees: attendeesData.length }))
            }
          }

          // Fetch events count
          const eventsUrl = `${getAPIBaseURL()}/v1/admin/events`
          const eventsRes = await getData(eventsUrl, headers, { limit: 0 })
          if (eventsRes.error === '' && eventsRes.result.status === 200) {
            const eventsData = eventsRes.result.data?.data
            if (Array.isArray(eventsData)) {
              setStats(prev => ({ ...prev, events: eventsData.length }))
            }
          }

          // Fetch disputes count
          const disputesUrl = `${getAPIBaseURL()}/v1/disputes`
          const disputesRes = await getData(disputesUrl, headers, { limit: 0 })
          if (disputesRes.error === '' && disputesRes.result.status === 200) {
            const disputesData = disputesRes.result.data?.data
            if (Array.isArray(disputesData)) {
              const openDisputes = disputesData.filter(d => d.status === 'open').length
              setStats(prev => ({ ...prev, disputes: openDisputes }))
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch dashboard stats", err)
        setError("Failed to load dashboard statistics")
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const cards = [
    { 
      name: 'Total Centers', 
      value: stats.centers, 
      icon: BuildingOfficeIcon, 
      color: 'text-blue-600', 
      bg: 'bg-blue-100', 
      link: '/admin/centers' 
    },
    { 
      name: 'Total Attendees', 
      value: stats.attendees, 
      icon: UserGroupIcon, 
      color: 'text-purple-600', 
      bg: 'bg-purple-100', 
      link: '/admin/attendees' 
    },
    { 
      name: 'Total Events', 
      value: stats.events, 
      icon: CalendarDaysIcon, 
      color: 'text-cyan-600', 
      bg: 'bg-cyan-100', 
      link: '/admin/events' 
    },
    { 
      name: 'Total Orders', 
      value: stats.orders, 
      icon: ShoppingBagIcon, 
      color: 'text-amber-600', 
      bg: 'bg-amber-100', 
      link: '/admin/orders' 
    },
    { 
      name: 'Total Revenue', 
      value: formatCurrency(stats.revenue), 
      icon: BanknotesIcon, 
      color: 'text-green-600', 
      bg: 'bg-green-100', 
      link: '/admin/payments' 
    },
    { 
      name: 'Total Tickets', 
      value: stats.tickets, 
      icon: TicketIcon, 
      color: 'text-indigo-600', 
      bg: 'bg-indigo-100', 
      link: '/admin/tickets' 
    },
    { 
      name: 'Open Disputes', 
      value: stats.disputes, 
      icon: ExclamationTriangleIcon, 
      color: 'text-red-600', 
      bg: 'bg-red-100', 
      link: '/admin/disputes' 
    }
  ]

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard Overview</h1>
        <p className="text-slate-500 mt-1">Welcome back to the Obule Admin Console.</p>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-100">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card) => {
          const Icon = card.icon
          return (
            <Link 
              key={card.name} 
              to={card.link} 
              className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-500">{card.name}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">
                    {loading ? (
                      <span className="inline-block w-12 h-8 bg-slate-200 animate-pulse rounded"></span>
                    ) : (
                      card.value
                    )}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${card.bg} ${card.color} group-hover:scale-110 transition-transform flex-shrink-0`}>
                  <Icon className="w-6 h-6" />
                </div>
              </div>
            </Link>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-bold text-slate-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link 
              to="/admin/events" 
              className="block p-4 rounded-xl border border-slate-200 hover:bg-slate-50 transition"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Manage Events</p>
                  <p className="text-sm text-slate-500">View and manage all events</p>
                </div>
                <CalendarDaysIcon className="w-5 h-5 text-slate-400" />
              </div>
            </Link>
            <Link 
              to="/admin/offers" 
              className="block p-4 rounded-xl border border-slate-200 hover:bg-slate-50 transition"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Manage Offers</p>
                  <p className="text-sm text-slate-500">View and manage all offers</p>
                </div>
                <ShoppingBagIcon className="w-5 h-5 text-slate-400" />
              </div>
            </Link>
            <Link 
              to="/admin/disputes" 
              className="block p-4 rounded-xl border border-slate-200 hover:bg-slate-50 transition"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Review Disputes</p>
                  <p className="text-sm text-slate-500">Resolve customer disputes</p>
                </div>
                <ExclamationTriangleIcon className="w-5 h-5 text-slate-400" />
              </div>
            </Link>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-bold text-slate-900 mb-4">Platform Statistics</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Active Centers</span>
              <span className="font-bold text-slate-900">
                {loading ? '...' : stats.centers}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Total Events</span>
              <span className="font-bold text-slate-900">
                {loading ? '...' : stats.events}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Total Orders</span>
              <span className="font-bold text-slate-900">
                {loading ? '...' : stats.orders}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Total Revenue</span>
              <span className="font-bold text-slate-900">
                {loading ? '...' : formatCurrency(stats.revenue)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminHomePage
